import { useEffect, useState, useContext, useCallback } from 'react';

// import UserContext from '../store/user-context';
import { ModalContext } from '../store/modal-context';
import Login from '../components/Pages/Welcome/Login/Login';
import { getLocalUser } from '../components/Helpers/Util/User';

const useFetch = (requestConfig, decode = true) => {
  const [config, setConfig] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  // const { user, setUser } = useContext(UserContext);
  const { handleModal } = useContext(ModalContext);
  const user = getLocalUser();

  const initFunc = useCallback(
    (requestConfig) => {
      const jwtToken = {};
      const refreshToken = {};
      const { version } = window['appConfig'];

      const nonAuthUrls = [
        '/user/login',
        '/user/register',
        '/user/confirm',
        '/user/resendconfirmationcode',
        '/user/forgotpassword',
        '/user/resetpassword',
        '/user/refreshaccess',
      ];

      const routeIncludesNoAuthUrl = nonAuthUrls.some((url) => {
        if (requestConfig.url.includes(url)) {
          return true;
        }

        return false;
      });

      if (!routeIncludesNoAuthUrl) {
        if (user) {
          if (user.jwtToken) {
            jwtToken.Authorization = `Bearer ${user.jwtToken}`;
          }

          if (user.refreshToken) {
            refreshToken.refreshToken = `${user.refreshToken}`;
          }

          //Add jwt to headers or create headers if they don't exist
          if (
            Object.prototype.hasOwnProperty.call(
              requestConfig.options,
              'headers',
            )
          ) {
            requestConfig.options.headers = {
              ...requestConfig.options.headers,
              ...jwtToken,
              ...refreshToken,
            };
          } else {
            const headerOption = `headers: {${jwtToken}, ${refreshToken}}`;
            requestConfig.options = { ...requestConfig.options, headerOption };
          }
        }
      }

      requestConfig.options.headers = {
        ...requestConfig.options.headers,
        'app-version': version,
        // eslint-disable-next-line no-undef
        'app-build': process.env.REACT_APP_BUILD_VERSION,
      };

      setConfig(requestConfig);
    },
    [user],
  );

  const sendResponse = async () => {
    let data = '';

    try {
      const response = await fetch(config.url, config.options);

      //check if unauthenticated
      if (response.status === 401) {
        //don't run if request is for login or sign up
        //TODO: check if this can be done in a better way

        if (
          config.url.includes('/login') ||
          config.url.includes('/user/resetpassword') ||
          config.url.includes('/user/forgotpassword')
        ) {
          data = await response.json();
          throw new Error(data.error);
        }

        if (
          !config.url.includes('/login') ||
          !config.url.includes('/resetpassword') ||
          !config.url.includes('/forgotpassword')
        ) {
          const attemptRefresh = await refreshToken();

          if (attemptRefresh !== true) {
            setLoading(false);
            handleModal(<Login />, 'Session Expired', 'Please login again.');
            return;
          }

          // setReRun(true);
          return;
        }
      }

      //Manually throw error if 404 as 4xx and 5xx responses aren't network errors
      if (response.status === 404) {
        throw new Error(
          "The url you're looking for does not exist. Please try again later",
        );
      }

      if (response.ok === true) {
        const contentType = response.headers.get('content-type');

        if (decode === true) {
          if (contentType && contentType.indexOf('application/json') !== -1) {
            data = await response.json();
          } else if (
            contentType &&
            contentType.indexOf('application/octet-stream') !== -1
          ) {
            data = await response.blob();
          } else {
            data = await response.text();
          }
        }

        //Update access token so we don't get a 401
        if (response.headers.has('authorization')) {
          let auth = response.headers.get('authorization');

          await updateItem('user', 'jwtToken', auth);
        }

        setData(data);
      }

      if (response.ok === false) {
        let respError = await response.json();
        // console.log(respError)

        //check for 400
        if (response.status === 400) {
          //check if request is for login
          if (config.url.includes('/login')) {
            setData(respError);
          }
        }

        throw new Error(respError.error);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Fetch Error:');
      console.error(error);
      setError(error.message || error);
    }
  };

  const refreshToken = async () => {
    if (!Object.prototype.hasOwnProperty.call(user, 'refreshToken'))
      return false;
    const refreshOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `refreshToken=${user.refreshToken}`,
    };

    const { apiUrl } = window['appConfig'];
    const response = await fetch(
      `${apiUrl}/user/refreshaccess`,
      refreshOptions,
    );

    if (response.status !== 200) {
      return false;
    }

    const data = await response.json();
    // setUser((prevState) => ({
    //   ...prevState,
    //   jwtToken: data.jwtToken,
    //   refreshToken: data.refreshToken,
    // }));
    await updateItem('user', 'jwtToken', data.jwtToken);
    await updateItem('user', 'refreshToken', data.refreshToken);

    const jwtToken = `Bearer ${data.jwtToken}`;

    setConfig((prevState) => ({
      ...prevState,
      options: {
        // object that we want to update
        ...prevState.options, // keep all other key-value pairs
        headers: {
          //go again...
          ...prevState.options.headers,
          Authorization: jwtToken, // update the value of specific key
        },
      },
    }));

    return true;
  };

  async function updateItem(key, property, value) {
    // console.log(`updating the propery ${property} on ${key} with value ${value} `)
    let obj = JSON.parse(localStorage.getItem(key));
    obj[property] = value;
    localStorage.setItem(key, JSON.stringify(obj));
  }

  useEffect(() => {
    if (config !== null) {
      setLoading(true);
      setData(null);
      setError(null);
      sendResponse();
    }

    return () => {
      setLoading(false);
    };
  }, [config]);

  return { data, loading, error, initFunc };
};

export default useFetch;
